if (!customElements.get('cart-gift-note')) {
  // One draft and one request queue for the page and drawer, including section replacements.
  const state = { initialized: false, enabled: false, message: '', saved: '', timer: null, queue: Promise.resolve() };
  const components = new Set();

  function renderAll() {
    components.forEach((component) => component.render());
  }

  function save() {
    clearTimeout(state.timer);
    const task = state.queue.catch(() => {}).then(async () => {
      const message = state.enabled ? state.message : '';
      if (message === state.saved) return;
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ attributes: { 'Hediye notu': message } })
      });
      if (!response.ok) throw new Error('Gift note could not be saved');
      const cart = await response.json();
      if (cart.errors || (cart.attributes?.['Hediye notu'] || '') !== message) {
        throw new Error('Gift note was not persisted');
      }
      state.saved = message;
      components.forEach((component) => { component.error.hidden = true; });
    });
    state.queue = task;
    return task;
  }

  class CartGiftNote extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.textarea = this.querySelector('textarea');
      this.fields = this.querySelector('.cart-gift-note__fields');
      this.counter = this.querySelector('[data-gift-count]');
      this.error = this.querySelector('[role="alert"]');
      this.form = this.textarea.form;
      if (!state.initialized) {
        state.message = this.textarea.value;
        state.enabled = this.checkbox.checked;
        state.saved = this.textarea.value;
        state.initialized = true;
      }
      components.add(this);
      this.onToggle = () => {
        state.enabled = this.checkbox.checked;
        if (!state.enabled) state.message = '';
        renderAll();
        this.scheduleSave();
        if (state.enabled) this.textarea.focus();
      };
      this.onInput = () => {
        state.message = this.textarea.value.slice(0, this.textarea.maxLength);
        renderAll();
        this.scheduleSave();
      };
      this.onSubmit = this.submit.bind(this);
      this.checkbox.addEventListener('change', this.onToggle);
      this.textarea.addEventListener('input', this.onInput);
      this.form?.addEventListener('submit', this.onSubmit);
      this.render();
    }

    disconnectedCallback() {
      components.delete(this);
      this.checkbox.removeEventListener('change', this.onToggle);
      this.textarea.removeEventListener('input', this.onInput);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    render() {
      this.checkbox.checked = state.enabled;
      this.checkbox.setAttribute('aria-expanded', String(state.enabled));
      this.fields.hidden = !state.enabled;
      this.textarea.value = state.message;
      this.textarea.required = state.enabled;
      this.textarea.setCustomValidity(state.enabled && !state.message.trim() ? this.textarea.dataset.requiredMessage : '');
      this.counter.textContent = state.message.length;
    }

    scheduleSave() {
      clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        save().catch(() => {
          components.forEach((component) => { component.error.hidden = false; });
        });
      }, 300);
    }

    async submit(event) {
      if (this.allowSubmit) return;
      event.preventDefault();
      if (this.submitting) return;
      this.submitting = true;
      const submitter = event.submitter;
      try {
        // Wait for older writes and flush the latest draft before navigating to checkout.
        do {
          await save();
        } while ((state.enabled ? state.message : '') !== state.saved);
        // A resolved save can resume between submit listeners. Wait until the
        // original submission has finished: browsers ignore reentrant requestSubmit.
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (!this.isConnected || !this.form.isConnected) return;
        this.allowSubmit = true;
        this.form.requestSubmit(submitter || undefined);
      } catch (error) {
        this.error.hidden = false;
      } finally {
        this.allowSubmit = false;
        this.submitting = false;
      }
    }
  }

  customElements.define('cart-gift-note', CartGiftNote);
}
